import React, { useState } from "react";
import { Sidebar } from "../sidebar/Sidebar";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Divider,
  Field,
  Switch,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
  Tab,
  TabList,
  InfoLabel
} from "@fluentui/react-components";
import { FormattedMessage, useIntl } from "react-intl";
import {
  ArrowDownload20Filled,
  Copy20Regular,
  Delete20Regular,
  FullScreenMaximize20Filled,
  Link20Filled,
  LinkDismiss20Filled
} from "@fluentui/react-icons";
import NumericInput from "../fluent/NumericInput";
import { ObjectInspectArtwork, SaveArtworkSettings } from "./ObjectInspectArtwork";
import { useUploadFormContext } from "../contexts/UploadFormContext";
import Scrubber from "../input/Scrubber";
import { MediumFrameContainer } from "../layout/Artworks/MediumFrameContainer";

const useStyles = makeStyles({
  objectInspectSidebar: {
    width: "100vw",
    "@media (min-width: 576px)": {
      width: "500px"
    }
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingHorizontalL
  },
  button: {
    "&:disabled": {
      backgroundColor: "transparent",
      opacity: 0.5
    }
  },
  deleteButton: {
    border: "1px solid #EB5757",
    "& svg": {
      color: "#EB5757",
      fill: "#EB5757"
    },
    // On disabled do keep it outline without background but less opacity
    "&:disabled": {
      backgroundColor: "transparent",
      opacity: 0.5
    }
  },
  label: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap"
  },
  noWrap: {
    // Target the label, and make min-width: 60px
    "& label": {
      minWidth: "55px"
    },

    "@media (min-width: 576px)": {
      flexWrap: "nowrap"
    }
  },
  accordionPanel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    "@media (min-width: 576px)": {
      gap: tokens.spacingVerticalMNudge
    }
  },
  input: {
    minWidth: "unset",
    "@media (min-width: 576px)": {
      minWidth: "200px",
      width: "200px"
    }
  },
  compoundNumericInput: {
    display: "flex",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalMNudge,
    "@media (min-width: 576px)": {
      gap: tokens.spacingHorizontalL
    },
    "& > div": {
      display: "flex",
      alignItems: "center",
      gap: tokens.spacingHorizontalMNudge
    }
  },
  axisInput: {
    maxWidth: "71px",
    borderLeft: "3px solid" // Placeholder, this will be dynamically changed below
  },
  axisColorX: {
    borderLeftColor: "red"
  },
  axisColorY: {
    borderLeftColor: "green"
  },
  axisColorZ: {
    borderLeftColor: "blue"
  },
  active: {
    color: "var(--primary-color)"
  },
  accordion: {
    // Target fui-AccordionItem
    "& > div": {
      marginBottom: tokens.spacingVerticalL
    }
  },
  focused: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  dropdown: {
    "& *": {
      textTransform: "capitalize"
    }
  },
  footer: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: "#16181E"
  }
});

const ActionButton = ({ action, styles }) => {
  const button = action.href ? (
    <Button
      as="a"
      key={action.key}
      href={action.href}
      icon={action.icon}
      target="_blank"
      className={mergeClasses(styles.button, action.styles ?? "")}
      rel="noreferrer"
      disabled={action.disabled}
    />
  ) : (
    <Button
      key={action.key}
      icon={action.icon}
      onClick={action.onClick}
      text={action.text}
      className={mergeClasses(styles.button, action.styles ?? "")}
      disabled={action.disabled}
    />
  );

  return action.tooltip ? (
    <Tooltip content={action.tooltip} key={action.key}>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

ActionButton.propTypes = {
  action: PropTypes.object,
  styles: PropTypes.object
};

export function ObjectInspectSidebar({
  onClose,
  onToggleCastShadow,
  onToggleReceiveShadow,
  onClone,
  onDelete,
  canDelete,
  onTogglePersistency,
  onToggleVisible,
  isPinned,
  onTogglePhysics,
  physics,
  onChangeMass,
  onChangeGravity,
  visible,
  onTransform,
  transform,
  canEdit,
  canTransform,
  canClone,
  onDownload,
  onCopy,
  canPin,
  focusedObject,
  onFocusObject,
  currentTab,
  hasArtwork = false,
  tabs,
  onChangeTab,
  isAvatar,
  isMe,
  hid,
  onToggleHideAvatar,
  propertiesTabs,
  shadow,
  canEditMedia,
  isArtwork,
  hasMediumOrFrame
}) {
  const styles = useStyles();
  const intl = useIntl();

  const actions = [
    {
      icon: <FullScreenMaximize20Filled />,
      key: "maximize",
      onClick: onFocusObject,
      text: intl.formatMessage({ id: "object-menu.maximize-button", defaultMessage: "Maximize" }),
      tooltip: intl.formatMessage({ id: "object-menu.maximize-button", defaultMessage: "Maximize" })
    },
    {
      icon: <Link20Filled />,
      key: "link",
      text: intl.formatMessage({ id: "object-menu.link-button", defaultMessage: "Copy link" }),
      tooltip: intl.formatMessage({ id: "object-menu.link-button", defaultMessage: "Copy link" }),
      onClick: onCopy
    },
    {
      icon: <Copy20Regular />,
      key: "copy",
      onClick: onClone,
      text: intl.formatMessage({ id: "object-menu.copy-button", defaultMessage: "Copy" }),
      tooltip: intl.formatMessage({ id: "object-inspect-sidebar.clone-tooltip", defaultMessage: "Clone" }),
      disabled: !canClone
    },
    {
      icon: <ArrowDownload20Filled />,
      key: "download",
      onClick: onDownload,
      text: intl.formatMessage({ id: "object-menu.download-button", defaultMessage: "Download" })
    },
    {
      icon: <Delete20Regular />,
      key: "delete",
      text: intl.formatMessage({ id: "object-menu.delete-button", defaultMessage: "Delete" }),
      tooltip: intl.formatMessage({ id: "object-inspect-sidebar.delete-tooltip", defaultMessage: "Delete" }),
      styles: styles.deleteButton,
      onClick: onDelete,
      disabled: !canDelete
    }
  ];

  const [scaleAspectRatio, setScaleAspectRatio] = useState(false);

  const handleTransformChange = (field, axis, value) => {
    const newTransform = {
      ...transform,
      [field]: {
        ...transform[field],
        [axis]: value
      }
    };
    onTransform(newTransform);
  };

  const handleScaleAspectRatio = () => {
    setScaleAspectRatio(!scaleAspectRatio);
  };

  const handleScaleChange = (axis, value) => {
    const newScale = { ...transform.scale };

    if (scaleAspectRatio) {
      // Set the same value for all axes
      newScale.x = value;
      newScale.y = value;
      newScale.z = value;
    } else {
      // Update only the specified axis
      newScale[axis] = value;
    }

    onTransform({ ...transform, scale: newScale });
  };

  const { onSubmitArtworkProperties, handleSubmit, loadingUpload } = useUploadFormContext();

  return (
    <Sidebar
      title={<FormattedMessage id="object-inspect-sidebar.title" defaultMessage="Properties" />}
      position="end"
      onClose={onClose}
      className={styles.objectInspectSidebar}
      as="form"
      onSubmit={hasArtwork ? handleSubmit(onSubmitArtworkProperties) : undefined}
      footerClassName={styles.footer}
      footer={currentTab == propertiesTabs.artworks ? <SaveArtworkSettings isPinned={isPinned} /> : <></>}
    >
      <div className={styles.content}>
        {!isAvatar && !isMe && (
          <>
            <div className={styles.actions}>
              {actions.map(action => (
                <ActionButton key={action.key} action={action} styles={styles} />
              ))}
            </div>
            {hasArtwork && canEdit && (
              <TabList defaultSelectedValue={currentTab}>
                <>
                  {tabs.map(tab => (
                    <Tab
                      key={tab.value}
                      disabled={
                        (tab.value == propertiesTabs.artworks && !canEdit) ||
                        (tab.value == propertiesTabs.properties &&
                          loadingUpload &&
                          currentTab == propertiesTabs.artworks)
                      }
                      value={tab.value}
                      onClick={() => onChangeTab(tab.value)}
                    >
                      {tab.text}
                    </Tab>
                  ))}
                </>
              </TabList>
            )}
            <Divider />
          </>
        )}
        {currentTab == propertiesTabs.artworks && <ObjectInspectArtwork isPinned={isPinned} />}
        {currentTab == propertiesTabs.properties && (
          <Accordion defaultOpenItems={["1", "2", "3", "4", "5", "6"]} collapsible multiple className={styles.accordion}>
            {isAvatar && !!focusedObject && (
              <>
                <AccordionItem value="1">
                  <AccordionHeader>
                    <FormattedMessage id="object-inspect-sidebar.hide-avatar-header" defaultMessage="Hide Avatar" />
                  </AccordionHeader>
                  <AccordionPanel className={styles.accordionPanel}>
                    <Field
                      className={styles.label}
                      orientation="horizontal"
                      label={<FormattedMessage id="object-inspect-sidebar.hide-avatar-label" defaultMessage="Hide" />}
                    >
                      <Switch checked={hid} onChange={onToggleHideAvatar} />
                    </Field>
                  </AccordionPanel>
                </AccordionItem>
                <Divider />
              </>
            )}
            {canEdit && (
              <>
                {canPin && (
                  <>
                    <AccordionItem value="2">
                      <AccordionHeader>
                        <FormattedMessage
                          id="object-inspect-sidebar.persistency-header"
                          defaultMessage="Persistency & Lock"
                        />
                      </AccordionHeader>
                      <AccordionPanel className={styles.accordionPanel}>
                        <Field className={styles.label} orientation="horizontal" label={"Enable"}>
                          <Switch checked={isPinned} onChange={onTogglePersistency} />
                        </Field>
                      </AccordionPanel>
                    </AccordionItem>
                    <Divider />
                  </>
                )}
                <AccordionItem value="3">
                  <AccordionHeader>
                    <FormattedMessage id="object-inspect-sidebar.transform-header" defaultMessage="Transform" />
                  </AccordionHeader>
                  <AccordionPanel className={styles.accordionPanel}>
                    <Field
                      className={mergeClasses(styles.label, styles.noWrap)}
                      orientation="horizontal"
                      label={"Position"}
                    >
                      <div className={styles.compoundNumericInput}>
                        {["x", "y", "z"].map(axis => (
                          <div key={`position-${axis}`}>
                            <Scrubber
                              value={transform.position[axis]}
                              onChange={value => handleTransformChange("position", axis, value)}
                              disabled={!canTransform}
                            >
                              {axis.toUpperCase()}
                            </Scrubber>
                            <NumericInput
                              value={transform.position[axis]}
                              appearance="filled-darker"
                              onChange={value => handleTransformChange("position", axis, value)}
                              step={0.01}
                              className={mergeClasses(styles.axisInput, styles[`axisColor${axis.toUpperCase()}`])}
                              disabled={!canTransform}
                            />
                          </div>
                        ))}
                      </div>
                    </Field>
                    <Field
                      className={mergeClasses(styles.label, styles.noWrap)}
                      orientation="horizontal"
                      label={"Rotation"}
                    >
                      <div className={styles.compoundNumericInput}>
                        {["x", "y", "z"].map(axis => (
                          <div key={`rotation-${axis}`}>
                            <Scrubber
                              value={transform.rotation[axis]}
                              onChange={value => handleTransformChange("rotation", axis, value)}
                              disabled={!canTransform}
                            >
                              {axis.toUpperCase()}
                            </Scrubber>
                            <NumericInput
                              value={transform.rotation[axis]}
                              appearance="filled-darker"
                              onChange={value => handleTransformChange("rotation", axis, value)}
                              step={0.01}
                              className={mergeClasses(styles.axisInput, styles[`axisColor${axis.toUpperCase()}`])}
                              disabled={!canTransform}
                            />
                          </div>
                        ))}
                      </div>
                    </Field>
                    <Field
                      className={mergeClasses(styles.label, styles.noWrap)}
                      orientation="horizontal"
                      label={isArtwork ? {
                        children: (_, props) => (
                          <InfoLabel
                            {...props}
                            info="Scale can't be modified in artworks"
                          >
                            {intl.formatMessage({ id: "object-inspect-sidebar.scale-label", defaultMessage: "Scale" })}
                          </InfoLabel>
                        )
                      }
                      : intl.formatMessage({ id: "object-inspect-sidebar.scale-label", defaultMessage: "Scale" })
                      }
                    >
                      <div className={styles.compoundNumericInput}>
                        {/* Transparent icon button to keep the aspect ratio */}

                        {["x", "y", "z"].map(axis => (
                          <div key={`scale-${axis}`}>
                            {axis === "x" && (
                              <Button
                                appearance="transparent"
                                icon={!scaleAspectRatio ? <Link20Filled /> : <LinkDismiss20Filled />}
                                onClick={handleScaleAspectRatio}
                                // className={styles.aspectRatioButton}
                                className={mergeClasses(styles.aspectRatioButton, scaleAspectRatio && styles.active)}
                                disabled={!canTransform || isArtwork}
                              />
                            )}
                            <Scrubber
                              value={transform.scale[axis]}
                              onChange={value => handleScaleChange(axis, value)}
                              step={0.01}
                              disabled={!canTransform || isArtwork}
                            >
                              {axis.toUpperCase()}
                            </Scrubber>
                            <NumericInput
                              value={transform.scale[axis]}
                              appearance="filled-darker"
                              onChange={value => handleScaleChange(axis, value)}
                              step={0.01}
                              className={mergeClasses(styles.axisInput, styles[`axisColor${axis.toUpperCase()}`])}
                              disabled={!canTransform || isArtwork}
                            />
                          </div>
                        ))}
                      </div>
                    </Field>
                  </AccordionPanel>
                </AccordionItem>
                <Divider />
                <AccordionItem value="4">
                  <AccordionHeader>
                    <FormattedMessage id="object-inspect-sidebar.rendering-header" defaultMessage="Rendering" />
                  </AccordionHeader>
                  <AccordionPanel className={styles.accordionPanel}>
                    <Field
                      className={styles.label}
                      orientation="horizontal"
                      label={<FormattedMessage id="object-inspect-sidebar.visible-label" defaultMessage="Visible" />}
                    >
                      <Switch checked={visible} onChange={onToggleVisible} disabled={!canTransform} />
                    </Field>
                    <Field
                      className={styles.label}
                      orientation="horizontal"
                      label={
                        <FormattedMessage id="object-inspect-sidebar.cast-shadow-label" defaultMessage="Cast Shadow" />
                      }
                    >
                      <Switch onChange={onToggleCastShadow} disabled={!canTransform} checked={shadow.cast} />
                    </Field>
                    <Field
                      className={styles.label}
                      orientation="horizontal"
                      label={
                        <FormattedMessage
                          id="object-inspect-sidebar.receive-shadow-label"
                          defaultMessage="Receive Shadow"
                        />
                      }
                    >
                      <Switch onChange={onToggleReceiveShadow} disabled={!canTransform} checked={shadow.receive} />
                    </Field>
                  </AccordionPanel>
                </AccordionItem>
                <Divider />
                <AccordionItem value="5">
                  <AccordionHeader>
                    <FormattedMessage id="object-inspect-sidebar.physics-header" defaultMessage="Physics" />
                  </AccordionHeader>
                  <AccordionPanel className={styles.accordionPanel}>
                    <Field
                      className={styles.label}
                      orientation="horizontal"
                      label={<FormattedMessage id="object-inspect-sidebar.enable-label" defaultMessage="Enable" />}
                    >
                      <Switch disabled={!canTransform} checked={physics?.enabled} onChange={onTogglePhysics} />
                    </Field>
                    {/* <Field
                      className={styles.label}
                      orientation="horizontal"
                      label={
                        <FormattedMessage id="object-inspect-sidebar.body-type-label" defaultMessage="Body Type" />
                      }
                    >
                      <DropdownSelect
                        appearance="filled-darker"
                        value={physics.type}
                        options={optionsBodyType}
                        onChange={(options, value) => onChangeBodyType(value.value)}
                        className={mergeClasses(styles.input, styles.dropdown)}
                        disabled={!canTransform || !physics.enabled}
                        inlinePopup
                      />
                    </Field> */}
                    <Field
                      className={styles.label}
                      orientation="horizontal"
                      label={<FormattedMessage id="object-inspect-sidebar.mass-label" defaultMessage="Mass" />}
                    >
                      <NumericInput
                        value={physics.mass || 0}
                        appearance="filled-darker"
                        onChange={onChangeMass}
                        className={styles.input}
                        disabled={!canTransform || !physics.enabled}
                      />
                    </Field>
                    <Field
                      className={styles.label}
                      orientation="horizontal"
                      label={<FormattedMessage id="object-inspect-sidebar.gravity-label" defaultMessage="Gravity" />}
                    >
                      <NumericInput
                        value={physics?.gravity?.y || 0}
                        appearance="filled-darker"
                        onChange={onChangeGravity}
                        className={styles.input}
                        disabled={!canTransform || !physics.enabled}
                        step={0.1}
                      />
                    </Field>
                  </AccordionPanel>
                </AccordionItem>
                {(canEditMedia || hasMediumOrFrame) && (
                  <>
                    <Divider />
                    <AccordionItem value="6">
                      <AccordionHeader>
                        <FormattedMessage id="object-inspect-sidebar.media-header" defaultMessage="Media" />
                      </AccordionHeader>
                      <AccordionPanel className={styles.accordionPanel}>
                        <MediumFrameContainer disabled={isPinned} styles={styles} />
                      </AccordionPanel>
                    </AccordionItem>
                  </>
                )}

              </>
            )}
          </Accordion>
        )}
      </div>
    </Sidebar>
  );
}

ObjectInspectSidebar.propTypes = {
  item: PropTypes.object,
  onClose: PropTypes.func,
  onInspect: PropTypes.func,
  onLink: PropTypes.func,
  onDownload: PropTypes.func,
  onToggleCastShadow: PropTypes.func,
  onToggleReceiveShadow: PropTypes.func,
  onTogglePhysics: PropTypes.func,
  onChangeMass: PropTypes.func,
  onChangeGravity: PropTypes.func,
  onChangeBodyType: PropTypes.func,
  onToggleVisible: PropTypes.func,
  onDelete: PropTypes.func,
  onTogglePersistency: PropTypes.func,
  visible: PropTypes.bool,
  physics: PropTypes.object,
  onClone: PropTypes.func,
  isPinned: PropTypes.bool,
  name: PropTypes.string,
  canDelete: PropTypes.bool,
  onTransform: PropTypes.func,
  transform: PropTypes.shape({
    position: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
      z: PropTypes.number
    }),
    rotation: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
      z: PropTypes.number
    }),
    scale: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
      z: PropTypes.number
    })
  }).isRequired,
  BODYTYPE: PropTypes.object,
  canEdit: PropTypes.bool,
  canTransform: PropTypes.bool,
  canClone: PropTypes.bool,
  onCopy: PropTypes.func,
  canPin: PropTypes.bool,
  focusedObject: PropTypes.object,
  onFocusObject: PropTypes.func,
  tabs: PropTypes.array,
  currentTab: PropTypes.string,
  onChangeTab: PropTypes.func,
  hasArtwork: PropTypes.bool,
  isAvatar: PropTypes.bool,
  isMe: PropTypes.bool,
  hid: PropTypes.bool,
  onToggleHideAvatar: PropTypes.func,
  propertiesTabs: PropTypes.object,
  shadow: PropTypes.object
};

// Add default value for name
ObjectInspectSidebar.defaultProps = {
  name: "ObjectName.png",
  BODYTYPE: {
    KINEMATIC: 1,
    DYNAMIC: 2
  },
  propertiesTabs: {
    properties: "Properties",
    artworks: "Artwork"
  }
};
